import React from "react";
import { RotateCw } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getHeadersWithUniqueVisitorId } from "@brandclub/common-ui";
import moment from "moment";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

interface Conversation {
  conversationId: string;
  preview: string;
  title?: string;
  lastUpdated: string;
}

interface ConversationHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  onConversationSelect?: (conversationId: string) => void;
}

// Helper function to format timestamps using moment.js
const formatTimestamp = (timestamp: string): string => {
  const m = moment(timestamp);
  const now = moment();
  const diffInMinutes = now.diff(m, "minutes");
  const diffInHours = now.diff(m, "hours");
  const diffInDays = now.diff(m, "days");

  if (diffInMinutes < 1) {
    return "Just now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else if (diffInDays === 1) {
    return "Yesterday";
  } else if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  } else {
    // Format as "Jan 15" or "Jan 15, 2024" if not current year
    const formatString = m.year() === now.year() ? "MMM D" : "MMM D, YYYY";
    return m.format(formatString);
  }
};

// API function to fetch conversations
const fetchConversations = async (): Promise<{
  conversations: Conversation[];
}> => {
  // Get authentication headers including JWT token (if signed in) and visitor ID
  const headers = await getHeadersWithUniqueVisitorId();

  const response = await fetch("/api-rewards/concierge/conversations", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...headers, // Includes Authorization, x-bc-visitor-id, X-Client-AppId
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch conversations");
  }

  return response.json();
};

export const ConversationHistoryDrawer: React.FC<ConversationHistoryDrawerProps> =
  ({ open, onClose, onConversationSelect }) => {
    const { data, isLoading, isFetching, error, refetch } = useQuery<{
      conversations: Conversation[];
    }>({
      queryKey: ["conversations"],
      queryFn: fetchConversations,
      enabled: open, // Only fetch when drawer is open
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime in v5)
    });

    const handleConversationClick = (conversationId: string) => {
      onConversationSelect?.(conversationId);
      onClose();
    };

    return (
      <Drawer
        open={open}
        onOpenChange={(isOpen) => !isOpen && onClose()}
        direction="left"
      >
        <DrawerContent className="tw:h-full tw:w-80 tw:rounded-none">
          <div className="tw:flex tw:h-full tw:flex-col tw:bg-white">
            {/* Header */}
            <DrawerHeader className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:px-3 tw:py-3 tw:space-y-0">
              <DrawerTitle className="tw:text-sm tw:font-semibold">
                Chats
              </DrawerTitle>
              <button
                onClick={() => refetch()}
                disabled={isFetching}
                aria-label="Refresh conversations"
                className="tw:inline-flex tw:h-8 tw:w-8 tw:items-center tw:justify-center tw:rounded-lg tw:text-gray-600 hover:tw:bg-gray-100 hover:tw:text-gray-900 tw:transition-colors disabled:tw:opacity-50 tw:cursor-pointer"
              >
                <RotateCw
                  size={16}
                  strokeWidth={2}
                  className={isFetching ? "tw:animate-spin" : ""}
                />
              </button>
            </DrawerHeader>

            {/* Content */}
            <div className="tw:flex-1 tw:overflow-y-auto">
              {isLoading && (
                <div className="tw:flex tw:items-center tw:justify-center tw:p-4">
                  <div className="tw:h-5 tw:w-5 tw:animate-spin tw:rounded-full tw:border-2 tw:border-gray-300 tw:border-t-gray-900" />
                </div>
              )}

              {error && (
                <div className="tw:mx-3 tw:my-2 tw:rounded-lg tw:bg-red-50 tw:p-3 tw:text-sm tw:text-red-800">
                  Failed to load conversations. Please try again.
                </div>
              )}

              {data && data.conversations.length === 0 && (
                <div className="tw:p-6 tw:text-center tw:text-sm tw:text-gray-500">
                  No conversations yet. Start a new conversation to see your
                  history here.
                </div>
              )}

              {data && data.conversations.length > 0 && (
                <div>
                  {data.conversations.map((conversation) => (
                    <button
                      key={conversation.conversationId}
                      onClick={() =>
                        handleConversationClick(conversation.conversationId)
                      }
                      className="tw:w-full tw:px-3 tw:py-2.5 tw:text-left tw:transition-colors tw:group tw:cursor-pointer"
                      style={{
                        backgroundColor: "transparent",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f9fafb";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <div className="tw:line-clamp-2 tw:text-sm tw:font-medium tw:text-gray-900">
                        {conversation.title || conversation.preview}
                      </div>
                      <div className="tw:mt-1 tw:text-xs tw:text-gray-500">
                        {formatTimestamp(conversation.lastUpdated)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    );
  };
